import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase-config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  Divider,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { GUEST_EXAM_LIMIT, guestExamsLeft } from '../utils/guestExams';

function ProfilePage({ onSelectPastExam }) {
  const navigate = useNavigate();
  const isGuest = Boolean(auth.currentUser?.isAnonymous);
  const [userProfile, setUserProfile] = useState(null);
  const [stats, setStats] = useState({ examsTaken: 0, averageScore: 0, highestScore: 0, totalCorrect: 0 });
  const [pastExams, setPastExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }

        const resultsRef = collection(db, 'results');
        const q = query(resultsRef, where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        
        let results = [];
        let totalScore = 0;
        let maxScore = -Infinity;
        let totalCorrect = 0;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          results.push({ id: doc.id, ...data });
          totalScore += (data.score || 0);
          totalCorrect += (data.correct || 0);
          if ((data.score || 0) > maxScore) maxScore = data.score;
        });

        results.sort((a, b) => {
          const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
          const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
          return dateB - dateA;
        });

        setPastExams(results);
        setStats({
          examsTaken: results.length,
          averageScore: results.length > 0 ? (totalScore / results.length).toFixed(1) : 0,
          highestScore: results.length > 0 ? maxScore : 0,
          totalCorrect: totalCorrect
        });

      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      <Container maxWidth="md" disableGutters sx={{ mx: 0 }}>
        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', mb: 4, overflow: 'visible' }}>
          <Box sx={{ height: '100px', bgcolor: '#1d4ed8', borderRadius: '16px 16px 0 0' }} />
          <CardContent sx={{ pt: 0, px: { xs: 3, md: 5 }, pb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              alignItems: 'center', 
              mt: { xs: '-50px', md: '-60px' }, 
              mb: 4, 
              gap: { xs: 2, md: 3 } 
            }}>
              <Avatar 
                src={auth.currentUser?.photoURL} 
                sx={{ 
                  width: { xs: 100, md: 120 }, 
                  height: { xs: 100, md: 120 }, 
                  border: '6px solid #ffffff', 
                  bgcolor: '#f97316', 
                  fontSize: '48px', 
                  fontWeight: 800,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              >
                {isGuest ? 'G' : getInitials(userProfile?.name || auth.currentUser?.displayName)}
              </Avatar>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '24px', md: '34px' }, color: '#ffffff', lineHeight: 1.1, mb: 2 }}>
                  {isGuest ? 'Guest' : userProfile?.name || auth.currentUser?.displayName || 'User'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'center', gap: 1.5, mt: 1 }}>
                  <Chip label={isGuest ? 'Guest' : 'Student'} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, borderRadius: '4px', height: '26px' }} />
                  <Typography sx={{ color: '#64748b', fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px' }}>
                    #{auth.currentUser?.uid?.substring(0, 6).toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 7 }}>
                {isGuest ? (
                  <Box sx={{ p: 3, borderRadius: '12px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <Typography sx={{ fontWeight: 700, color: '#1e3a8a', fontSize: '17px', mb: 1 }}>
                      You're practising as a guest
                    </Typography>
                    <Typography sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.6, mb: 2 }}>
                      {guestExamsLeft() > 0
                        ? `${guestExamsLeft()} of ${GUEST_EXAM_LIMIT} free exams left. `
                        : `You've used your ${GUEST_EXAM_LIMIT} free exams. `}
                      Sign in with Google to keep these results on your account and practise without limits.
                    </Typography>
                    <Button variant="contained" disableElevation onClick={() => navigate('/login')} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}>
                      Sign in to save progress
                    </Button>
                  </Box>
                ) : (
                <>
                <Typography sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', mb: 2, fontSize: '12px', letterSpacing: '0.05em' }}>
                  Personal Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: '8px', display: 'flex' }}>
                      <EmailIcon sx={{ color: '#475569', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Email Address</Typography>
                      <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '15px' }}>{userProfile?.email || auth.currentUser?.email}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: '8px', display: 'flex' }}>
                      <PhoneIcon sx={{ color: '#475569', fontSize: '20px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Phone Number</Typography>
                      <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '15px' }}>{userProfile?.phone || 'N/A'}</Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: '8px', display: 'flex' }}>
                          <CalendarMonthIcon sx={{ color: '#475569', fontSize: '20px' }} />
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Date of Birth</Typography>
                          <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '15px' }}>{userProfile?.dob || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: '8px', display: 'flex' }}>
                          <SchoolIcon sx={{ color: '#475569', fontSize: '20px' }} />
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Class / Group</Typography>
                          <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '15px' }}>{userProfile?.class} - {userProfile?.combination}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                </>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <Typography sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', mb: 2, fontSize: '12px', letterSpacing: '0.05em' }}>
                  Performance Stats
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Exams Taken', value: stats.examsTaken, icon: <HistoryIcon />, color: '#3b82f6', bg: '#eff6ff' },
                    { label: 'Avg. Score', value: stats.averageScore, icon: <AssessmentIcon />, color: '#10b981', bg: '#ecfdf5' },
                    { label: 'High Score', value: stats.highestScore, icon: <WorkspacePremiumIcon />, color: '#f59e0b', bg: '#fffbeb' },
                    { label: 'Total Correct', value: stats.totalCorrect, icon: <AssessmentIcon />, color: '#c2410c', bg: '#fff7ed' }
                  ].map((stat, idx) => (
                    <Grid size={{ xs: 6 }} key={idx}>
                      <Box sx={{ p: 2, borderRadius: '12px', bgcolor: stat.bg, color: stat.color, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                        <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#1e293b' }}>{stat.value}</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{stat.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1e293b' }}>
              Exam History
            </Typography>
            <Button 
              variant="text" 
              onClick={() => setShowHistory(!showHistory)}
              endIcon={showHistory ? null : <ChevronRightIcon />}
              sx={{ fontWeight: 700, color: '#1d4ed8' }}
            >
              {showHistory ? 'Show Less' : 'View All'}
            </Button>
          </Box>

          {(showHistory || pastExams.length > 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(showHistory ? pastExams : pastExams.slice(0, 5)).map((exam) => (
                <Card key={exam.id} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', '&:hover': { borderColor: '#1d4ed8', bgcolor: '#f8fafc' }, transition: '0.2s' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>
                          {exam.examId.startsWith('immediate_') ? 'Practice Test' : exam.examId}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b', mt: 0.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                          <Typography sx={{ fontSize: '12px' }}>{formatDate(exam.submittedAt)}</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box>
                          <Typography sx={{ color: '#94a3b8', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Score</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px' }}>{exam.score} <Typography component="span" sx={{ fontSize: '12px', color: '#64748b' }}>/ {exam.maxScore ?? exam.totalQuestions * 4}</Typography></Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <Box>
                          <Typography sx={{ color: '#94a3b8', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>Accuracy</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#10b981', fontSize: '14px' }}>
                            {Math.round((exam.correct / (exam.totalQuestions || 1)) * 100)}%
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => onSelectPastExam(exam)}
                          sx={{ 
                            borderRadius: '8px', 
                            textTransform: 'none', 
                            fontWeight: 700,
                            borderColor: '#e2e8f0',
                            color: '#1d4ed8',
                            '&:hover': { bgcolor: '#eff6ff', borderColor: '#1d4ed8' }
                          }}
                        >
                          View Response
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {!showHistory && pastExams.length > 5 && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '14px' }}>
                    + {pastExams.length - 5} more exams in history
                  </Typography>
                </Box>
              )}
              
              {pastExams.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed #e2e8f0', bgcolor: 'transparent', borderRadius: '12px' }}>
                  <Typography sx={{ color: '#94a3b8' }}>No exams attempted yet.</Typography>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default ProfilePage;
